import type { Company } from 'interfaces/content/Company';
import * as S from 'pages/CompanyEarningsReport/CompanyEarningsReportStyles';
import CustomLink from 'components/Globals/Base/Link';
import classNames from 'classnames';
import { formatDateTime } from 'utils/dateUtils';
import ErrorBoundary from 'components/ErrorBoundary';
import TrinityPlayer from 'components/TrinityPlayer';
import { useCallback, useContext, useRef, useEffect, useState } from 'react';
import PDFViewer from 'components/Company/PDFViewer';
import dynamic from 'next/dynamic';
import getAdPlacementConfig, { type AdPlacementConfigProps } from 'services/Ad/config/adPlacementConfig';
import { UserPaywallContext } from 'providers/PaywallProvider';
import { UIFragments } from 'utils/log/constants/uiFragments';
import type { ArticlePreview } from 'interfaces/content/articles/Articles';
import type { EventData } from 'services/Gtm/entities';
import { UserContext } from 'providers/UserProvider';
import type { EarningsReport } from 'api/EarningsReport/getReportBySlug';
import type { QuartrSpeaker } from 'pages/CompanyEarningsReport/QuartrTranscript';
import PoweredByQuartr from 'components/Company/EarningsReportsList/PoweredByQuartr';
import SocialShareDropdown from 'components/SocialShareDropdown';
import SocialLinks from 'components/SocialLinks';
import getEarningReportPageTestId from 'constants/testsIds/earningsReportPage';

const ContentWithEmbed = dynamic(() => import('components/ContentWithEmbed'), {
  ssr: false,
});

const Outbrain = dynamic(() => import('components/Outbrain'), {
  ssr: false,
});

const LatestPopularContent = dynamic(() => import('components/ArticleV2/Latest'), {
  ssr: false,
});

const quartrAssetsUrl = process?.env?.SITE_BASE_URL?.includes('localhost')
  ? 'https://develop.fortune.com'
  : process?.env?.SITE_BASE_URL || '';

export interface CompanyQuarterlyEarningsProps {
  company: Company;
  earningsReport: EarningsReport;
  latestPosts: { posts: ArticlePreview[] };
  subdomain: string;
}

const CompanyEarningsReport = ({ company, earningsReport, latestPosts, subdomain }: CompanyQuarterlyEarningsProps) => {
  const ticker = company?.companyInfo?.Ticker;
  const [participants, setParticipants] = useState(new Map<number, QuartrSpeaker>());

  const slideDocument = earningsReport.documents?.find((doc) => doc.documentType === 'Slides');
  const [pdfUrl, setPdfUrl] = useState('');
  const [audioUrl, setAudioUrl] = useState('');

  // Check if the S3 file for the PDF we have on record is accessible
  useEffect(() => {
    function setAlternativeUrl() {
      if (slideDocument?.fileSourceUrl) {
        setPdfUrl(slideDocument?.fileSourceUrl || '');
      }
    }

    let tmpPdfUrl = slideDocument?.filePath || '';

    if (tmpPdfUrl) {
      tmpPdfUrl = quartrAssetsUrl + tmpPdfUrl;
      fetch(tmpPdfUrl, {
        method: 'HEAD',
      })
        .then((data) => {
          if (!data.ok) {
            setAlternativeUrl();
            return;
          }

          setPdfUrl(tmpPdfUrl);
        })
        .catch(() => {
          setAlternativeUrl();
        });
    } else {
      setAlternativeUrl();
    }
  }, []);

  // Check if the S3 file for the audio we have on record is accessible
  useEffect(() => {
    function setAlternativeUrl() {
      if (earningsReport.audio?.fileSourceUrl || earningsReport.audio?.fileStreamUrl) {
        setAudioUrl(earningsReport.audio?.fileSourceUrl || earningsReport.audio?.fileStreamUrl);
      }
    }
    let tmpAudioUrl = earningsReport.audio?.filePath || '';

    if (tmpAudioUrl) {
      tmpAudioUrl = quartrAssetsUrl + tmpAudioUrl;

      fetch(tmpAudioUrl, {
        method: 'HEAD',
      })
        .then((data) => {
          if (!data.ok) {
            setAlternativeUrl();
            return;
          }

          setAudioUrl(tmpAudioUrl);
        })
        .catch(() => {
          setAlternativeUrl();
        });
    }
  }, []);

  useEffect(() => {
    const tmpParticipants = new Map<number, QuartrSpeaker>();
    (earningsReport.transcript?.paragraphs || []).forEach((paragraph) => {
      const speakerMap = earningsReport.transcript?.speaker_mapping?.find(
        (speakerItem) => speakerItem.speaker_id === paragraph.speaker,
      );
      const speakerData =
        speakerMap ||
        ({
          speaker_name: `Speaker #${paragraph.speaker}`,
        } as QuartrSpeaker);

      if (!tmpParticipants.has(paragraph.speaker)) {
        tmpParticipants.set(paragraph.speaker, speakerData);
      }
    });

    setParticipants(tmpParticipants);
  }, [earningsReport]);

  let lastSpeaker = -1;

  const [eventDate, setEventDate] = useState('');
  const [adFrequencyViewPort, setAdFrequencyViewPort] = useState(0);
  const adPlacementInfo: AdPlacementConfigProps = getAdPlacementConfig(null);

  const getViewportWidth = useCallback(() => window.innerWidth, []);
  useEffect(() => {
    const initialViewportWidth = getViewportWidth();
    if (initialViewportWidth < 768) {
      setAdFrequencyViewPort(adPlacementInfo.mobileAdFrequency);
      return;
    }

    setAdFrequencyViewPort(adPlacementInfo.desktopAdFrequency);
  }, [adPlacementInfo, getViewportWidth]);

  useEffect(() => {
    if (earningsReport.date) {
      setEventDate(formatDateTime(earningsReport.date, 'datetime'));
    }
  }, [earningsReport]);
  const { paywallCheck } = useContext(UserPaywallContext).paywallState;

  const trinityRef = useRef<HTMLDivElement>(null);

  let title = (
    <CustomLink
      href={company.slug}
      className='link'
    >
      {company.title}
    </CustomLink>
  );
  if (ticker) {
    title = (
      <>
        {company.title}{' '}
        <CustomLink
          href={company.slug}
          className='link'
        >
          ({ticker})
        </CustomLink>
      </>
    );
  }

  const { userState: user } = useContext(UserContext);
  const trackingData: EventData = {
    loginStatusCD: user.isLoggedIn ? 'logged-in' : 'guest',
    platformCD: 'own',
    sessionIDCD: '',
    userAgentCD: typeof window !== 'undefined' ? window.navigator?.userAgent : '',
  };

  const latestModuleTrackingData: EventData = {
    ...trackingData,
    contentPlacementCD: 'latest - bottom',
    eventAction: 'latest module click',
    eventCategory: 'navigation',
  };

  const trinityUrl = earningsReport.slug ? quartrAssetsUrl + earningsReport.slug : '';
  const pageTitle = `${company.title} ${ticker ? `(${ticker})` : ''} ${
    earningsReport.title || ''
  } Earnings Call Transcript`;

  return (
    <S.Wrapper>
      <S.Header>
        <div className='back-link'>
          <CustomLink
            href={company.slug}
            data-cy={getEarningReportPageTestId('BACK_LINK')}
          >
            <S.PreviousArrow data-cy={getEarningReportPageTestId('BACK_ICON')} /> {company.title}
          </CustomLink>
        </div>
        <h1
          className='header-line'
          data-cy={getEarningReportPageTestId('HEADER_LINE')}
        >
          {pageTitle}
        </h1>
      </S.Header>
      <S.ContentWrapper>
        <S.ShareContainer>
          <S.QuartrContainer data-cy={getEarningReportPageTestId('QUARTR_CONTAINER')}>
            <PoweredByQuartr />
          </S.QuartrContainer>
          <S.MobileShareContainer data-cy={getEarningReportPageTestId('MOBILE_SOCIAL_SHARE_CONTAINER')}>
            <SocialShareDropdown
              withoutMargins
              className='social-links'
              title={pageTitle}
            />
          </S.MobileShareContainer>
          <S.DesktopShareContainer data-cy={getEarningReportPageTestId('DESKTOP_SOCIAL_SHARE_CONTAINER')}>
            <SocialLinks title={pageTitle} />
          </S.DesktopShareContainer>
        </S.ShareContainer>
        <S.TopGroup data-cy={getEarningReportPageTestId('TOP_GROUP')}>
          <div>
            <S.Intro data-cy={getEarningReportPageTestId('INTRO')}>
              <strong data-cy={getEarningReportPageTestId('INTRO_TITLE')}>{title}</strong>
              <p data-cy={getEarningReportPageTestId('INTRO_EARNING_REPORT')}>
                {earningsReport.title || ''} Earnings Call
              </p>
              <p data-cy={getEarningReportPageTestId('INTRO_DATE')}>{eventDate}</p>
            </S.Intro>
            <S.ParticipantsHeader data-cy={getEarningReportPageTestId('PARTICIPANTS_HEADER')}>
              Company Participants
            </S.ParticipantsHeader>
            <S.Participants
              data-cy={getEarningReportPageTestId('PARTICIPANTS_CONTAINER')}
              className='impression-element'
              data-content-placement='company_participants'
            >
              {[...participants].map((item, index) => {
                const info = [];
                if (item[1]?.speaker_role && item[1].speaker_role.toUpperCase() !== 'RESERVED_OPERATOR') {
                  info.push(item[1].speaker_role);
                }
                if (item[1]?.speaker_company && item[1].speaker_company.toUpperCase() !== 'RESERVED_OPERATOR') {
                  info.push(item[1].speaker_company);
                }
                return (
                  <li key={`${item[1].speaker_name}}`}>
                    <strong data-cy={`${getEarningReportPageTestId('PARTICIPANT_NAME')}-${index}`}>
                      {item[1].speaker_name}
                    </strong>
                    {info.length ? ` - ${info.join(', ')}` : null}
                  </li>
                );
              })}
            </S.Participants>
          </div>
          <ErrorBoundary>
            {audioUrl ? (
              <S.TrinityPlayerContainer
                data-cy={getEarningReportPageTestId('TRINITY_PLAYER_CONTAINER')}
                ref={trinityRef}
                className='impression-element'
                data-content-placement='trinity_audio_player'
              >
                <TrinityPlayer
                  url={trinityUrl}
                  ownAudio={audioUrl}
                  noTrinityAds
                  paywallCheck={paywallCheck}
                  trinityRef={trinityRef}
                  microsite={subdomain}
                />
              </S.TrinityPlayerContainer>
            ) : null}
          </ErrorBoundary>
        </S.TopGroup>
        <ErrorBoundary>{pdfUrl ? <PDFViewer urlPDF={pdfUrl} /> : null}</ErrorBoundary>
        <ContentWithEmbed
          embedInstructions={[]}
          adFrequency={adFrequencyViewPort}
          lastPWithoutAds={0}
          section={company.primarySection.name}
          articleUrl={company.url}
        >
          {[
            <div
              data-cy={getEarningReportPageTestId('CONTENT_WITH_EMBED')}
              key={`earnings-page-${adFrequencyViewPort}`}
              data-key={`earnings-page-${adFrequencyViewPort}`}
            >
              {(earningsReport.transcript?.paragraphs || []).map((paragraph, index) => {
                const newSpeaker = lastSpeaker !== paragraph.speaker;
                const withAd = (index + 1) % adFrequencyViewPort === 0;
                lastSpeaker = paragraph.speaker;

                return (
                  <S.SpeakerText
                    data-cy={`${getEarningReportPageTestId('SPEAKER_TEXT')}-${index}`}
                    key={paragraph.start}
                    className={classNames({
                      'new-speaker': newSpeaker,
                      'with-ad': withAd,
                    })}
                  >
                    {newSpeaker ? (
                      <S.Author data-cy={`${getEarningReportPageTestId('AUTHOR_SPEAKER_TEXT')}-${index}`}>
                        {participants.get(paragraph.speaker)?.speaker_name}
                      </S.Author>
                    ) : null}
                    {paragraph.text}
                  </S.SpeakerText>
                );
              })}
            </div>,
          ]}
        </ContentWithEmbed>
        {process.env.FEATURE_FLAG_EARNINGS_OUTBRAIN && (
          <ErrorBoundary fragment={UIFragments.EARNINGS_REPORT}>
            <S.Footer>
              <Outbrain
                key='1'
                permalink=''
                placement='footer'
              />
            </S.Footer>
          </ErrorBoundary>
        )}
      </S.ContentWrapper>
      <ErrorBoundary fragment={UIFragments.ARTICLE_PAGE}>
        {latestPosts && (
          <LatestPopularContent
            content={latestPosts}
            trackingData={latestModuleTrackingData}
            title='Latest in Fortune 500'
          />
        )}
      </ErrorBoundary>
    </S.Wrapper>
  );
};

export default CompanyEarningsReport;
